<script setup lang="ts">
import { EmbeddedDropdown } from '#shared'
import { useInfiniteQuery } from '@tanstack/vue-query'
import { computed, useTemplateRef } from 'vue'
import { personalChatsQueryOptions } from '#entities/chat'
import { useInfiniteScroll } from '#shared/lib/misc/use-infinite-scroll'
import ConversationsDropdownEmptyState from './conversations-dropdown-empty-state.vue'
import ConversationsDropdownItem from './conversations-dropdown-item.vue'

const { toggleEl } = defineProps<{ toggleEl: HTMLElement }>()

const emit = defineEmits<{ close: [] }>()

const personalChatsQuery = useInfiniteQuery(personalChatsQueryOptions)
const personalChats = computed(() => personalChatsQuery.data.value?.pages.flatMap(page => page.data) ?? [])
const sortedPersonalChats = computed(() =>
  personalChats.value.sort(
    (chatA, chatB) =>
      new Date(chatB.lastMessage?.createdAt ?? '').getTime() - new Date(chatA.lastMessage?.createdAt ?? '').getTime()
  )
)

const listEl = useTemplateRef('listEl')
useInfiniteScroll(listEl, {
  direction: 'down',
  canLoadDown: computed(() => personalChatsQuery.hasNextPage.value && !personalChatsQuery.isFetching.value),
  loadDown: () => personalChatsQuery.fetchNextPage(),
})

const onConversationLinkClick = () => emit('close')
</script>

<template>
  <EmbeddedDropdown :toggleEl="toggleEl">
    <template #title> Conversations </template>

    <template #content>
      <ul v-if="personalChats.length > 0" :class="$style.list" ref="listEl">
        <ConversationsDropdownItem
          v-for="chat in sortedPersonalChats"
          :key="chat.id"
          :chat="chat"
          @link-click="onConversationLinkClick"
        />
      </ul>

      <ConversationsDropdownEmptyState v-else-if="personalChatsQuery.isPending.value" label="Loading..." />

      <ConversationsDropdownEmptyState v-else />
    </template>
  </EmbeddedDropdown>
</template>

<style lang="scss" module>
@use '#mixins/scrollbar' as scrollbar;

.list {
  min-height: 200px;
  max-height: 470px;
  overflow: auto;
  list-style: none;
  padding: 0;
  margin: 0;

  @include scrollbar.thick(#1f1f22);
}
</style>
